// Build-time mount point for the dashboard.
//
// Empty string when served at the origin root (current default). When the
// build is produced for an upstream proxy that mounts the SPA at a sub-path
// (e.g., '/forethought/dashboard'), BASE_PATH is that prefix — with no
// trailing slash — so it can be concatenated directly with a leading-slash
// route (BASE_PATH + '/foo').
//
// The value comes from the BASE_PATH env var at build time; see
// rsbuild.config.ts. Router basename, Amplify redirects, and any absolute
// in-app redirects must go through this constant / withBase() so a single
// build flag flips the entire mount.
export const basePath: string = BASE_PATH;

/**
 * Prefix an absolute in-app path (e.g., '/logout') with the build's BASE_PATH.
 * When BASE_PATH is empty, returns the path unchanged. Path must start with '/'.
 *
 * `base` defaults to the build's BASE_PATH and is only overridden by tests,
 * since BASE_PATH is substituted at build time and can't be stubbed at runtime.
 */
export function withBase(path: string, base: string = basePath): string {
  return base + path;
}

/**
 * Remove the build's BASE_PATH from the start of a pathname, returning the
 * in-app path (always starting with '/'). When BASE_PATH is empty, or the
 * path doesn't begin with BASE_PATH, the input is returned unchanged.
 *
 * Use this when comparing window.location.pathname against route strings or
 * extracting positional segments — both break under a mounted deployment
 * because BASE_PATH shifts the leading segments.
 *
 * `base` defaults to the build's BASE_PATH and is only overridden by tests.
 */
export function stripBase(pathname: string, base: string = basePath): string {
  if (!base || !pathname.startsWith(base)) {
    return pathname;
  }
  // Require a path-segment boundary after the base so that a pathname like
  // `/foo-other` doesn't get stripped when base is `/foo`.
  const rest = pathname.slice(base.length);
  if (rest !== '' && !rest.startsWith('/')) {
    return pathname;
  }
  return rest || '/';
}
